import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useGraphColors } from "@core";

import { BaseTimeSeriesChart, TimeSeriesData, YAxisConfig } from "../charts/BaseTimeSeriesChart";
import type { DateRange, GraphSettings, Resolution, TypedChartSeries } from "../shared/types";

// Series types for indoor temperature charts
export enum IndoorTemperatureSeriesType {
  AVERAGE_TEMPERATURE = "average_temperature",
  TARGET_TEMPERATURE = "target_temperature",
}

type IndoorTemperatureChartContainerProps = {
  data: TypedChartSeries<IndoorTemperatureSeriesType>[];
  isLoading?: boolean;
  chartHeight?: number;
  dateRange?: DateRange;
  resolution?: Resolution;
  graphSettings?: GraphSettings;
};

/**
 * IndoorTemperatureChartContainer - Container for indoor temperature time series charts
 *
 * This component displays average indoor temperature and target temperature (when optimization is enabled)
 */
export const IndoorTemperatureChartContainer: React.FC<IndoorTemperatureChartContainerProps> = ({
  data,
  isLoading = false,
  chartHeight,
  dateRange,
  resolution,
  graphSettings,
}) => {
  const { t } = useTranslation(["propai_properties", "_common"]);
  const graphColors = useGraphColors();

  // Configuration for each series
  const INDOOR_TEMP_SERIES_CONFIG = useMemo((): Record<
    IndoorTemperatureSeriesType,
    {
      name: string;
      unit: string;
      color: string;
      yAxisIndex: number;
      visible: boolean;
      dashStyle?: string;
    }
  > => {
    return {
      [IndoorTemperatureSeriesType.AVERAGE_TEMPERATURE]: {
        name: t("propai:average_indoor_temperature"),
        unit: " °C",
        color: graphColors[0],
        yAxisIndex: 0,
        visible: true,
      },
      [IndoorTemperatureSeriesType.TARGET_TEMPERATURE]: {
        name: t("propai:target_temperature"),
        unit: " °C",
        color: graphColors[1],
        yAxisIndex: 0,
        visible: true,
        dashStyle: "Dash",
      },
    };
  }, [t]);

  // Y-axis configuration - single axis for both temperature series
  const Y_AXIS_CONFIGS = useMemo((): YAxisConfig[] => {
    return [
      {
        title: { text: `${t("text_indoor_temperature")} (°C)` },
        showLastLabel: true,
        showFirstLabel: true,
        softMin: 18,
        softMax: 26,
      },
    ];
  }, [t]);

  // Transform indoor temperature data to time series format
  const series = useMemo((): TimeSeriesData[] => {
    const result: TimeSeriesData[] = [];

    data.forEach((seriesData) => {
      const config = INDOOR_TEMP_SERIES_CONFIG[seriesData.type];
      if (config) {
        result.push({
          uid: seriesData.type,
          name: config.name,
          data: seriesData.data,
          unit: config.unit,
          color: config.color,
          yAxisIndex: config.yAxisIndex,
          visible: config.visible,
          dashStyle: config.dashStyle,
        });
      }
    });

    return result;
  }, [data, INDOOR_TEMP_SERIES_CONFIG]);

  return (
    <BaseTimeSeriesChart
      series={series}
      yAxisConfigs={Y_AXIS_CONFIGS}
      chartHeight={chartHeight}
      dateRange={dateRange}
      resolution={resolution}
      graphSettings={graphSettings}
      isLoading={isLoading}
      allowSeriesToggle={true}
      yAxisLayout="side-by-side"
    />
  );
};
